/**
 * Card - Componente de visualización de pedido en mobile
 *
 * Este componente muestra la información de un pedido en formato de tarjeta,
 * respetando las columnas seleccionadas por el usuario a través del ColumnSelector.
 * Las columnas 'ref' (número de pedido) y 'status' son fijas y siempre se muestran.
 */
import { StatusBadgeWithDot } from './StatusBadgeWithDot';
import { Shop as ShopComponent } from '@shared/components/ui/shop';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBagShopping } from '@fortawesome/pro-light-svg-icons';
import { DropdownActions } from '../interaction';
import { Checkbox } from '@shared/components/ui/checkbox';
import { useRef } from 'react';
import { ProductsModal, ProductsModalRef } from '../modals';
import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  defaultLogisticStatusDot,
  defaultOrderStatusDot,
  getStatusForDropdown,
  logisticStatusColorMap,
  statusColorMap,
} from '../../config/orderStatusConfig';

// 🎯 Función utilitaria para formatear fechas como dd/mm/yyyy HH:mm
const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return '-';

  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '-';

    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');

    return `${day}/${month}/${year} ${hours}:${minutes}`;
  } catch {
    return '-';
  }
};

const formatOrderTotal = (order: any): string => {
  const amount = order.total;
  const currency = order.currency || 'EUR';
  if (amount == null || Number.isNaN(Number(amount))) return '-';
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(Number(amount));
  } catch {
    return `${amount} ${currency}`;
  }
};

export interface CardProps {
  item: any; // Order
  className?: string;
  showActions?: boolean;
  showIndex?: boolean;
  isSelected?: boolean;
  onToggleSelect?: () => void;
  selectedColumns?: string[];
}

export const Card = React.memo(function Card({
  item: order,
  className = '',
  showActions = true,
  isSelected,
  onToggleSelect,
  selectedColumns,
}: CardProps) {
  const { t } = useTranslation('ordersModule');
  const errorCount = order.errors?.length || 0;
  const status = order.status;
  const orderStatusDot = statusColorMap[status.id] ?? defaultOrderStatusDot;

  // 🎯 Helper para verificar si una columna está visible
  const isColumnVisible = (columnId: string) => {
    if (!selectedColumns) return true; // Si no hay selectedColumns, mostrar todo
    return selectedColumns.includes(columnId);
  };

  const shipment = order.shipment;
  const logisticStatusBadge = (() => {
    if (!shipment) {
      return null;
    }

    const statusMapped = shipment.status_mapped;

    if (!statusMapped) {
      return (
        <StatusBadgeWithDot
          className="text-xs"
          dotClassName={defaultLogisticStatusDot.dot}
          label={t('table.no_logistic_status')}
        />
      );
    }

    if (typeof statusMapped === 'string') {
      return (
        <StatusBadgeWithDot className="text-xs" dotClassName={defaultLogisticStatusDot.dot} label={statusMapped} />
      );
    }

    const { dot } = logisticStatusColorMap[statusMapped.id] ?? defaultLogisticStatusDot;
    return <StatusBadgeWithDot className="text-xs" dotClassName={dot} label={statusMapped.name} />;
  })();
  const getStatusForDropdownLocal = (statusId: number) => {
    return getStatusForDropdown(statusId);
  };

  const productsModalRef = useRef<ProductsModalRef>(null);

  const handleOpenProductsModal = () => {
    const showNotAssigned = order.status?.id === 1 || order.status?.id === 2;
    const payload = {
      orderNumber: order.ref,
      stockMessage:
        'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.',
      // Asegurar nombre del producto: usar line.product.name si line.name es nulo
      products: order.lines.map((line) => ({
        name: line.name || line.product?.name || (line.pack ? line.pack.title : ''),
        image: line.product?.image_hash || '',
        badge: line.product?.code || (line.pack ? line.pack.title : ''),
        qty: line.qty,
        price: line.amount,
        subtotal: line.qty * line.amount,
      })),
      subtotal: order.lines.reduce((acc, line) => acc + line.qty * line.amount, 0),
      shipping: order.total_shipping,
      tax: order.total_tax,
      discount: order.total_discount,
      total: order.total,
      showNotAssignedAlert: showNotAssigned,
    } as const;
    productsModalRef.current?.openWithData(payload);
  };

  return (
    <div className={`py-4 border-b border-card-border hover:bg-muted/30 transition-colors ${className}`}>
      {/* Header - Pedido, estado y acciones (siempre visible) */}
      <div className="flex items-center gap-2 mb-2 justify-between">
        <div className="flex items-center gap-2">
          <Checkbox className="w-4 h-4" checked={!!isSelected} onCheckedChange={onToggleSelect} />
          {errorCount > 0 && (
            <span className="w-4 h-4 rounded-full bg-error-buttons text-title text-[10px] flex items-center justify-center">
              {errorCount}
            </span>
          )}
          <span className="font-medium text-base">{order.ref}</span>
          {/* Status siempre visible si la columna está seleccionada */}
          {isColumnVisible('status') && (
            <StatusBadgeWithDot className="text-xs" dotClassName={orderStatusDot.dot} label={status.description} />
          )}
        </div>
        {showActions && (
          <DropdownActions
            status={getStatusForDropdownLocal(status.id)}
            orderId={order.id}
            orderNumber={order.ref}
            channelId={order.shop?.channel_id}
            attachments={order.attachments}
          />
        )}
      </div>

      {/* Shop - Solo si la columna shop está visible */}
      {isColumnVisible('shop') && (
        <div className="flex items-center gap-2 mb-1">
          <ShopComponent name={order.shop.name} src={order.shop.image_hash} />
        </div>
      )}

      {/* Courier - Solo si la columna está visible */}
      {isColumnVisible('courier') && order.shipment.courier && (
        <div className="flex items-center gap-2 mb-1">
          <ShopComponent name={order.shipment.courier?.name} src={order.shipment.courier?.image_hash} />
        </div>
      )}

      {/* Cliente (team) - Solo si está visible y tiene datos */}
      {isColumnVisible('team') && order.team && (
        <div className="flex items-center gap-2 mb-1">
          <span className="text-sm text-complementary-text">Cliente:</span>
          <span className="text-sm font-medium">{order.team.name}</span>
        </div>
      )}

      {/* Información del pedido */}
      <div className="flex flex-col items-end gap-2">
        {/* Productos - Solo si la columna está visible */}
        {isColumnVisible('products') && (
          <span className="flex items-center gap-1">
            <span className="relative inline-block cursor-pointer" onClick={handleOpenProductsModal}>
              <FontAwesomeIcon icon={faBagShopping} className="w-3.5 h-3.5" />
              <span
                className={`absolute -top-1 -right-0 w-1.5 h-1.5 mt-2.5 rounded-full border border-black ${
                  order.status?.id === 1 || order.status?.id === 2 || order.status?.id === 9 ? 'bg-error' : 'bg-success'
                }`}
              ></span>
            </span>
            <span className="cursor-pointer" onClick={handleOpenProductsModal}>
              {order.lines?.reduce((acc: number, line: any) => acc + (line?.qty || 0), 0)}
            </span>
          </span>
        )}

        {/* Total - Solo si la columna está visible */}
        {isColumnVisible('total') && (
          <span className="text-sm font-semibold tabular-nums">{formatOrderTotal(order)}</span>
        )}

        {/* Cliente - Solo si la columna está visible */}
        {isColumnVisible('customer') && <span className="text-sm font-medium">{order.customer?.name || '-'}</span>}

        {/* Fecha de creación - Solo si la columna está visible */}
        {isColumnVisible('order_created_at') && (
          <span className="text-xs text-complementary-text">{formatDate(order.order_created_at)}</span>
        )}

        {/* Listo para preparar - Solo si la columna está visible */}
        {isColumnVisible('ready_to_prepare_at') && (
          <span className="text-xs text-complementary-text">
            {t('table.ready_to_prepare_at')}: {formatDate(order.ready_to_prepare_at)}
          </span>
        )}

        {/* Fecha de envío - Solo si la columna está visible */}
        {isColumnVisible('shipment_processed_at') && order.shipment?.processed_at && (
          <span className="text-xs text-complementary-text">Enviado: {formatDate(order.shipment.processed_at)}</span>
        )}

        {/* Estado logístico - Solo si la columna está visible */}
        {isColumnVisible('logistic_status') && shipment && logisticStatusBadge && (
          <span className="flex items-center gap-2">
            <span className="text-xs text-complementary-text">{t('table.logistic_status')}:</span>
            {logisticStatusBadge}
          </span>
        )}

        {/* Tracking number - Solo si la columna está visible */}
        {isColumnVisible('tracking_number') &&
          order.shipment?.tracking_number &&
          typeof order.shipment.tracking_number === 'string' && (
            <span className="text-xs text-complementary-text font-mono">
              Tracking: {order.shipment.tracking_number}
            </span>
          )}

        {/* Fecha última actualización logística - Solo si la columna está visible */}
        {isColumnVisible('date_tracking_update') && order.shipment?.date_tracking_update && (
          <span className="text-xs text-complementary-text">
            Última actualización: {formatDate(order.shipment.date_tracking_update)}
          </span>
        )}

        {/* Método de pago - Solo si la columna está visible */}
        {isColumnVisible('payment_method') &&
          order.external_payment_method &&
          typeof order.external_payment_method === 'string' && (
            <span className="text-xs text-complementary-text">Pago: {order.external_payment_method}</span>
          )}

        {/* Teléfono - Solo si la columna está visible */}
        {isColumnVisible('phone') && order.customer?.phone && typeof order.customer.phone === 'string' && (
          <span className="text-xs text-complementary-text">Tel: {order.customer.phone}</span>
        )}
      </div>
      <ProductsModal ref={productsModalRef} />
    </div>
  );
});
